import type { PortfolioOrganisationNode } from "@keydials/api-client";

/**
 * Pure grouping/filter/sort for the cross-org dashboard (KEY-133/134). Operates on a minimal {@link PortfolioRow}
 * so it can be unit-tested without the dial view model or the API. The saved-view config (KEY-134) serialises
 * to {@link ViewConfig}; it is frontend-defined and opaque to the server.
 */

export type Grouping = "organisation" | "definition" | "none";
export type SortKey = "name" | "recent" | "completion";

export interface ViewConfig {
  grouping: Grouping;
  search: string;
  /** Restrict to these owning organisations (empty/undefined = all). */
  organisationIds?: string[];
  /** Restrict to these source definitions (empty/undefined = all). */
  definitionIds?: string[];
  sort: SortKey;
}

/** The built-in view applied when the user has no saved default. */
export const DEFAULT_VIEW: ViewConfig = { grouping: "definition", search: "", sort: "name" };

/** A dial as the dashboard knows it: summary fields plus the completion derived from its loaded detail. */
export interface PortfolioRow {
  id: string;
  name: string;
  organisationId: string;
  organisationName: string;
  organisationParentId: string | null;
  definitionId: string;
  definitionName: string;
  createdAtUtc: string;
  /** 0..1; 0 when the detail hasn't loaded. */
  completion: number;
  /** True when this dial runs a version that is out for checking (KEY-283) — see {@link CHECKING_SECTION_KEY}. */
  isTesting: boolean;
}

/** A rendered group: a heading (org or definition) and its dials, with `depth` for org-tree indentation. */
export interface GroupSection {
  key: string;
  label: string;
  depth: number;
  rows: PortfolioRow[];
}

/** Parses a saved view's opaque config, falling back to the built-in default for anything missing/invalid. */
export function parseViewConfig(json: string | null | undefined): ViewConfig {
  if (!json) {
    return { ...DEFAULT_VIEW };
  }
  try {
    const raw = JSON.parse(json) as Partial<ViewConfig>;
    return {
      grouping: raw.grouping === "organisation" || raw.grouping === "none" ? raw.grouping : "definition",
      search: typeof raw.search === "string" ? raw.search : "",
      organisationIds: Array.isArray(raw.organisationIds) ? raw.organisationIds : undefined,
      definitionIds: Array.isArray(raw.definitionIds) ? raw.definitionIds : undefined,
      sort: raw.sort === "recent" || raw.sort === "completion" ? raw.sort : "name",
    };
  } catch {
    return { ...DEFAULT_VIEW };
  }
}

export function serialiseViewConfig(config: ViewConfig): string {
  return JSON.stringify(config);
}

/** Applies the search/organisation/definition filters then sorts. Pure: returns a new array. */
export function applyFilters(rows: readonly PortfolioRow[], config: ViewConfig): PortfolioRow[] {
  const term = config.search.trim().toLowerCase();
  const orgSet = config.organisationIds && config.organisationIds.length ? new Set(config.organisationIds) : null;
  const defSet = config.definitionIds && config.definitionIds.length ? new Set(config.definitionIds) : null;

  const filtered = rows.filter((r) => {
    if (orgSet && !orgSet.has(r.organisationId)) return false;
    if (defSet && !defSet.has(r.definitionId)) return false;
    if (term && !`${r.name} ${r.organisationName} ${r.definitionName}`.toLowerCase().includes(term)) return false;
    return true;
  });

  return sortRows(filtered, config.sort);
}

function sortRows(rows: PortfolioRow[], sort: SortKey): PortfolioRow[] {
  const copy = [...rows];
  switch (sort) {
    case "recent":
      // Newest first; ISO timestamps sort lexicographically.
      copy.sort((a, b) => (a.createdAtUtc < b.createdAtUtc ? 1 : a.createdAtUtc > b.createdAtUtc ? -1 : a.name.localeCompare(b.name)));
      break;
    case "completion":
      // Least complete first (most in need of attention), tie-broken by name.
      copy.sort((a, b) => a.completion - b.completion || a.name.localeCompare(b.name));
      break;
    default:
      copy.sort((a, b) => a.name.localeCompare(b.name));
  }
  return copy;
}

/**
 * Groups already-filtered rows for display. Row order within a group is preserved (so the caller's sort wins).
 * For "organisation", returns a pre-order walk of the org tree: each org with at least one dial in its subtree
 * appears once, `depth` giving its nesting level, `rows` the dials it owns directly. Ancestors with no direct
 * dials still appear as headers so children nest under them.
 */
export function groupInstances(
  rows: readonly PortfolioRow[],
  orgs: readonly PortfolioOrganisationNode[],
  config: ViewConfig,
): GroupSection[] {
  // Dials sent for checking are fenced off into their own section ahead of everything else (KEY-516),
  // whatever the grouping mode. Partitioning *before* the tree walk is what makes that free: the
  // remaining logic never sees them, so they can't also appear under their owning organisation (which
  // for a checking dial is the root Key Dials org — a header that would otherwise be pure noise).
  const checking = rows.filter((r) => r.isTesting);
  const rest = checking.length ? rows.filter((r) => !r.isTesting) : rows;
  const sections = groupRemaining(rest, orgs, config);
  if (checking.length) {
    sections.unshift({ key: CHECKING_SECTION_KEY, label: "Sent for checking", depth: 0, rows: checking });
  }
  return sections;
}

/** Section key for the "Sent for checking" group — the one section that isn't an org/definition. */
export const CHECKING_SECTION_KEY = "checking";

function groupRemaining(
  rows: readonly PortfolioRow[],
  orgs: readonly PortfolioOrganisationNode[],
  config: ViewConfig,
): GroupSection[] {
  if (config.grouping === "none") {
    return rows.length ? [{ key: "all", label: "All dials", depth: 0, rows: [...rows] }] : [];
  }

  if (config.grouping === "definition") {
    const byDef = new Map<string, GroupSection>();
    for (const row of rows) {
      let group = byDef.get(row.definitionId);
      if (!group) {
        group = { key: row.definitionId, label: row.definitionName, depth: 0, rows: [] };
        byDef.set(row.definitionId, group);
      }
      group.rows.push(row);
    }
    return [...byDef.values()].sort((a, b) => a.label.localeCompare(b.label));
  }

  return groupByOrganisation(rows, orgs);
}

function groupByOrganisation(rows: readonly PortfolioRow[], orgs: readonly PortfolioOrganisationNode[]): GroupSection[] {
  const nodeById = new Map(orgs.map((o) => [o.id, o]));
  const rowsByOrg = new Map<string, PortfolioRow[]>();
  for (const row of rows) {
    (rowsByOrg.get(row.organisationId) ?? rowsByOrg.set(row.organisationId, []).get(row.organisationId)!).push(row);
  }

  // Children indexed by parent; an org whose parent isn't in the set is treated as a root.
  const childrenByParent = new Map<string | null, PortfolioOrganisationNode[]>();
  for (const org of orgs) {
    const parentKey = org.parentId && nodeById.has(org.parentId) ? org.parentId : null;
    (childrenByParent.get(parentKey) ?? childrenByParent.set(parentKey, []).get(parentKey)!).push(org);
  }

  // True when an org or any descendant owns at least one (filtered) dial — keeps empty branches out.
  const subtreeHasRows = new Map<string, boolean>();
  const computeHasRows = (org: PortfolioOrganisationNode): boolean => {
    if (subtreeHasRows.has(org.id)) return subtreeHasRows.get(org.id)!;
    subtreeHasRows.set(org.id, false); // guard against cycles
    let has = (rowsByOrg.get(org.id)?.length ?? 0) > 0;
    for (const child of childrenByParent.get(org.id) ?? []) {
      has = computeHasRows(child) || has;
    }
    subtreeHasRows.set(org.id, has);
    return has;
  };
  for (const org of orgs) computeHasRows(org);

  const sections: GroupSection[] = [];
  const visit = (org: PortfolioOrganisationNode, depth: number) => {
    if (!subtreeHasRows.get(org.id)) return;
    sections.push({ key: org.id, label: org.name, depth, rows: rowsByOrg.get(org.id) ?? [] });
    for (const child of (childrenByParent.get(org.id) ?? []).slice().sort((a, b) => a.name.localeCompare(b.name))) {
      visit(child, depth + 1);
    }
  };
  for (const root of (childrenByParent.get(null) ?? []).slice().sort((a, b) => a.name.localeCompare(b.name))) {
    visit(root, 0);
  }

  // Any dials whose owning org wasn't in the tree (shouldn't happen, but never drop a dial).
  const placed = new Set(sections.flatMap((s) => s.rows.map((r) => r.id)));
  const orphans = rows.filter((r) => !placed.has(r.id));
  if (orphans.length) {
    sections.push({ key: "ungrouped", label: "Other", depth: 0, rows: [...orphans] });
  }

  return sections;
}
